/**
 * AttendeesBlockView — Phase 6 free-flow rewrite.
 *
 * The attendeesBlock is no longer an atom — it contains zero or
 * more editable attendeeGroup children. NodeViewContent renders
 * the children. Add-group buttons live in a non-editable footer
 * that dispatches inserts via the editor's command chain.
 */
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { Plus, Building, Users } from 'lucide-react';

export function AttendeesBlockView({ node, editor, getPos }: NodeViewProps) {
  const isEditable = editor.isEditable;
  const hasGroups = node.childCount > 0;

  // Read-only + empty → render nothing so finalized meetings without
  // attendees don't show an empty section header.
  if (!isEditable && !hasGroups) return null;

  /** Insert a new attendeeGroup at the end of this block's content. */
  const addGroup = (isOjb: boolean) => {
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos === null) return;
    const endOfBlock = pos + node.nodeSize - 1;
    editor
      .chain()
      .focus()
      .insertContentAt(endOfBlock, {
        type: 'attendeeGroup',
        attrs: { isOjb },
        content: [
          {
            type: 'firmText',
            content: isOjb ? [{ type: 'text', text: 'OJB' }] : [],
          },
          { type: 'membersText', content: [] },
        ],
      })
      .run();
  };

  return (
    <NodeViewWrapper
      className="tt-attendees-block"
      data-node="attendeesBlock"
    >
      {(isEditable || hasGroups) && (
        <h3
          contentEditable={false}
          className="mt-6 mb-3 select-none border-b border-smoke/30 pb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-pine"
        >
          Attendees
        </h3>
      )}

      {/* Editable children — attendeeGroup nodes. */}
      <NodeViewContent
        as="div"
        className="tt-attendees-block__groups space-y-1"
      />

      {isEditable && (
        <div
          contentEditable={false}
          className="mt-2 flex flex-wrap items-center gap-1.5"
        >
          <button
            type="button"
            onClick={() => addGroup(true)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-pine/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em] text-pine transition-colors hover:bg-pine/10"
          >
            <Building size={11} strokeWidth={2.5} />
            <Plus size={11} strokeWidth={2.5} />
            OJB group
          </button>
          <button
            type="button"
            onClick={() => addGroup(false)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-charcoal/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em] text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            <Users size={11} strokeWidth={2.5} />
            <Plus size={11} strokeWidth={2.5} />
            External group
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}
